// import Vue from 'vue';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _unset from 'lodash/unset';
import _remove from 'lodash/remove';
import _isObject from 'lodash/isObject';
import _isArray from 'lodash/isArray';
import _findIndex from 'lodash/findIndex';
import _cloneDeep from 'lodash/cloneDeep';
import { toRaw } from 'vue';
import store from '@/store';
import { isPresent, filterPrimaryKeys, ruid, removeMessagesFromObj } from '@/lib/utils';
import { constants } from '@/lib/constants';


// constants
const defaultKeys = [
  { key: '_id', defaultValue: null, isPrimaryKey: true },
  { key: '_isAdded', defaultValue: false, isPrimaryKey: false },
  { key: '_isDeleted', defaultValue: false, isPrimaryKey: false },
];

const ALSO_MANAGING = {
  CreditApplication: () => ['primaryapplicant.customer'],
  CustomerOrder: () => [
    'customer',
    'orders.adjustments.orderlines.servicedetails',
    'orders.adjustments.orderlines',
    'orders.adjustments.assignments',
    'orders.adjustments',
    'orders.orderlines.servicedetails',
    'orders.orderlines',
    'orders.assignments',
    'orders',
  ],
  GlReportGroup: () => ['glreportgroupdetails'],
  Invoice: () => ['discounts'],
  JournalCode: () => ['journalcodedetails'],
  Product: () => {
    const params = ['images'];
    if (store.getters['meta/gers_v12']) params.push('costs');
    return params;
  },
  TagFormat: () => ['images'],
  ProductReorder: () => ['productreorderdetails'],
  ProductReorderResult: () => ['productreorderresultdetails'],
  PurchaseOrder: () => ['purchaseorderdetails'],
  ReceivingAppointment: () => ['appointmentdetails'],
  Transfer: () => ['transferdetails'],
};

/**
 * AUTOUPDATE_BACKREFS records cases where a server @rule_property
 * references a backref, which the autoupdate framework must account for.
 */
const AUTOUPDATE_BACKREFS = {
  InvoiceDiscount: {
    invoice: '..',
  },
  Order: {
    // in this special case, we go all the way to root model with '/'
    // so that both these example resolve properly:
    //   * orders.maincustomerorder.customer
    //   * orders.adjustments.maincustomerorder.customer
    maincustomerorder: '/',
  },
  OrderLine: {
    order: '..',
  },
  ProductReorderResultDetail: {
    productreorderresult: '..',
  },
  PurchaseOrderDetail: {
    purchaseorder: '..',
  },
  ReceivingAppointmentDetail: {
    appointment: '..',
  },
};


// methods
const primaryKeys = state => class_name => {
  const schema = _get(state, `schema.${class_name}`);
  if (!schema) return [];
  return filterPrimaryKeys(schema);
};


const sequencedKeys = state => class_name => {
  const properties = _get(state, `schema.${class_name}.properties`);
  const keys = [];
  for (const prop of Object.keys(properties)) {
    const isSequenced = _get(properties, `${prop}.property_meta.sequenced`);
    if (isSequenced) keys.push(prop);
  }
  return keys;
};


const generatePk = (className, obj, metaState) => {
  const keys = primaryKeys(metaState)(className);
  let pk = '';
  for (const key of keys) {
    if (!isPresent(obj[key])) return ruid();
    if (pk) pk += constants.DELIM;
    pk += obj[key];
  }
  return pk || ruid();
};


const insertKey = ({ stateObj, keyToInsert, isPrimaryKey = false, valueToInsert = null, metaState }) => {
  if (!stateObj || !_isObject(stateObj) || !isPresent(stateObj._classname)) return stateObj;
  const obj = stateObj;
  Object.keys(obj).forEach((key) => {
    if (!key.startsWith('original') && _isObject(obj[key])) {
      if (_isArray(obj[key])) {
        obj[key].forEach((el, index) => {
          if (_isObject(obj[key][index])) {
            obj[key][index] = insertKey({ stateObj: el, keyToInsert, isPrimaryKey, valueToInsert, metaState });
          }
        });
      }
      else {
        obj[key] = insertKey({ stateObj: obj[key], keyToInsert, isPrimaryKey, valueToInsert, metaState });
      }
    }
  });
  if (isPrimaryKey) {
    if (!obj._id && obj.uid) {
      _set(obj, keyToInsert, obj.uid);
    }
    else if (!obj._id) {
      _set(obj, keyToInsert, generatePk(obj._classname, obj, metaState));
    }
  }
  else if (keyToInsert === 'uid' && !obj.uid) {
    _set(obj, keyToInsert, ruid());
  }
  else if (keyToInsert !== 'uid') {
    _set(obj, keyToInsert, valueToInsert);
  }
  return obj;
};


const insertKeys = (collection, obj, metaState) => {
  let stateObj = obj;
  collection.forEach((keyObj) => {
    stateObj = insertKey({
      stateObj,
      metaState,
      keyToInsert: keyObj.key,
      isPrimaryKey: keyObj.isPrimaryKey,
      valueToInsert: keyObj.defaultValue,
    });
  });
  return stateObj;
};


const pruneDeletedObjects = (object) => {
  const obj = object;
  Object.keys(obj).forEach((key) => {
    if (_isObject(obj[key])) {
      if (_isArray(obj[key])) {
        obj[key] = obj[key].filter((el) => {
          if (_isArray(el)) return pruneDeletedObjects(el);
          return !el || !el._isDeleted;
        });
      }
      else if (obj[key]._isDeleted) {
        _unset(obj, key);
      }
      else {
        obj[key] = pruneDeletedObjects(obj[key]);
      }
    }
  });
  return obj;
};


const processUpdateKeys = ({ state }, { path, collection, key, keys = {}, removeKeys = [], isMove = false }) => {
  /**
   * TODO: Possibly add guards in all the `update_value` mutations to prevent
   * adding unchanged keys to the clientchangeset. See issue #14231.
   */
  // all `key` handling is done here
  if (!state.autoUpdatePath) return;
  // note: to support nested autoUpdates we do not just get `record` from state
  // but use `autoUpdatePath` to get to the `record` we care about
  const record = _get(state, state.autoUpdatePath);
  let manageModel = state.autoUpdateManageModels[record._classname];
  let updPath = '';
  let chainPath = '';
  if (path.length > state.autoUpdatePath.length) {
    updPath = path.slice(state.autoUpdatePath.length + 1);
    chainPath = updPath.replace(/\[\d*\]/g, '');
  }
  if (chainPath) manageModel = _get(manageModel, chainPath);
  if (!manageModel) return;
  // create a unified obj of change keys (add/update/delete)
  if (key) keys[key] = true;
  removeKeys.forEach(k => { keys[k] = false; });
  const origkeys = Object.keys(keys);
  if (origkeys.includes('saleprice') &&
    (manageModel._classname === 'OrderLine' ||
      manageModel._classname === 'OrderDetail' ||
      manageModel._classname === 'ServiceDetail')) {
    // itemprice is the "real" database column.  Any change
    // to the "fake" saleprice needs to also be an itemprice change.
    origkeys.push('itemprice');
  }
  const changekeys = origkeys.filter((k) => _get(manageModel, `${k}._trigger`, false) || k.endsWith('_fieldauth'));
  const changeset = [];
  const stateObject = _get(record, updPath);
  if (collection) {
    let checkNull = false;
    if (!origkeys.length && !isMove) {
      /* if no keys are passed in, we are add/deleting from a collection
       * and we are here - passed the withoutAutoUpdate check.
       * We want to tell the server that all keys that have a value (not null/undef)
       * on the object have been touched and put them in changeset.
       * REFACTOR to use isPresent() so '' is not included.
       */
      origkeys.push(...Object.keys(manageModel));
      changekeys.push(...origkeys.filter((k) => _get(manageModel, `${k}._trigger`, false)));
      checkNull = true;
    }
    const idCollection = collection.map((obj) => obj._id);
    idCollection.forEach((_id) => {
      const idx = _findIndex(stateObject, (item) => item._id === _id);
      if (idx > -1) {
        // adding/updating collection
        changekeys.forEach(chg => {
          if (checkNull && _get(stateObject, `[${idx}].${chg}`, null) === null) return;
          const _path = updPath ? `${updPath}[${idx}].${chg}` : `[${idx}].${chg}`;
          changeset.push(_path);
        });
      }
      if (manageModel._trigger) {
        changeset.push(updPath);
      }
    });
  }
  else if (changekeys.length) {
    changekeys.forEach(chg => {
      const _path = updPath ? `${updPath}.${chg}` : chg;
      changeset.push(_path);
    });
  }
  else if (!origkeys.length && manageModel._trigger) {
    changeset.push(updPath);
  }
  if (changeset.length) state.autoUpdateChanges.push(...changeset);
};

/**
 * If source of action/event was user initiated, flag record as dirty directly from mutation.
 */
const handleDirtyState = (state, userInitiated) => {
  if (!state.isRecordDirty && userInitiated) state.isRecordDirty = true;
};

/* NOTE on the use of `withoutAutoUpdate` flag:
 * as a rule of thumb, autoUpdates should not be circumvented programatically.
 * We support `withoutAutoUpdate` for the purpose of adding empty/new lines to a collection
 * which do not make sense to update. Also, in this implementation of autoUpdates, we use
 * `withoutAutoUpdate` within `updateResponse()` > `updateForClass()` so that mutations
 * made by autoUpdates do not trigger new autoUpdates.
 */
// actions
const updateValue = async (ctx, { path, key, value, removeKey = false, withoutAutoUpdate = false, userInitiated = false }) => {
  const payload = { path, key, value, removeKey, userInitiated };
  ctx.commit('update_object', payload);
  if (!withoutAutoUpdate) processUpdateKeys(ctx, payload);
};


const updateMultipleValues = async (ctx, { keys, removeKeys, path, withoutAutoUpdate = false, userInitiated = false }) => {
  const payload = { keys, removeKeys, path, userInitiated };
  const { commit } = ctx;
  commit('update_multiple_values', payload);
  if (!withoutAutoUpdate) processUpdateKeys(ctx, payload);
};

/**
 * NOTE: Use clone to avoid retaining reference to the same object/array.
 */
const updateCollection = async (ctx, { collection, path, key, value, withoutAutoUpdate = false, userInitiated = false, clone = false }) => {
  const payload = { collection, path, key, value, userInitiated, clone };
  ctx.commit('update_collection', payload);
  if (!withoutAutoUpdate) processUpdateKeys(ctx, payload);
};


const updateCollectionMultipleValues = async (ctx, {
  keys,
  path,
  withoutAutoUpdate = false,
  skipCondition = null,
  userInitiated = false,
  skipReactivity = false,
}) => {
  // REFACTOR: updateCollectionMultipleValues should accept a collection like
  // updateCollection(), for consistency.  The caller may have already filtered it.
  let collection = _get(ctx, `state[${path}]`);
  if (skipCondition) collection = collection.filter(obj => !skipCondition(obj));
  if (!collection.length) return;
  const payload = { collection, keys, path, userInitiated, skipReactivity };
  const { commit, rootState } = ctx;
  for (let record of payload.collection) {
    if (!record._id) record = insertKeys(defaultKeys, record, rootState.meta);
  }
  commit('update_collection_multiple_values', payload);
  if (!withoutAutoUpdate) processUpdateKeys(ctx, payload);
};


const updateCollectionUniqueValues = async (ctx, { keys, path, withoutAutoUpdate = false, userInitiated = false }) => {
  const collection = _get(ctx, `state[${path}]`);
  const payload = { collection, keys, path, userInitiated };
  const { commit, rootState } = ctx;
  for (let record of collection) {
    if (!record._id) record = insertKeys(defaultKeys, record, rootState.meta);
  }
  commit('update_collection_unique_values', payload);
  if (!withoutAutoUpdate) processUpdateKeys(ctx, payload);
};


const addCollection = async (ctx, { collection, path, withoutAutoUpdate = false, isMove = false, userInitiated = false }) => {
  const payload = { collection, path, isMove, userInitiated };
  const { commit, rootState } = ctx;
  for (let record of payload.collection) {
    if (!record._id) record = insertKeys(defaultKeys, record, rootState.meta);
  }
  commit('add_collection', payload);
  if (!withoutAutoUpdate) processUpdateKeys(ctx, payload);
};


const removeCollection = async (ctx, { collection, path, withoutAutoUpdate = false, isMove = false, userInitiated = false }) => {
  const payload = { collection, path, isMove, userInitiated };
  ctx.commit('remove_collection', payload);
  if (!withoutAutoUpdate) processUpdateKeys(ctx, payload);
};


const removeMessages = ({ commit }, payload) => {
  commit('remove_messages', payload);
};


// mutations
const update_object = (state, payload) => {
  const stateObject = _get(state, payload.path);
  removeMessagesFromObj(stateObject, payload.key);
  if (payload.removeKey) {
    _unset(stateObject, payload.key);
  }
  else {
    _set(stateObject, `${payload.key}`, payload.value);
  }
  handleDirtyState(state, payload.userInitiated);
};


// @todo TODO: collapse update_object and update_multiple_values into 1 method?
const update_multiple_values = (state, payload) => {
  const stateObject = _get(state, payload.path);
  for (const [key, value] of Object.entries(payload.keys)) {
    removeMessagesFromObj(stateObject, key);
    _set(stateObject, `${key}`, value);
  }
  if (payload.removeKeys) {
    for (const key of payload.removeKeys) {
      if (stateObject[`${key}`]) _unset(stateObject, `${key}`);
    }
  }
  handleDirtyState(state, payload.userInitiated);
};


const update_collection = (state, payload) => {
  const idCollection = payload.collection.map((obj) => obj._id);
  const stateObject = _get(state, payload.path);
  stateObject.forEach((obj) => {
    if (idCollection.includes(obj._id)) {
      if (payload.clone) {
        _set(obj, `${payload.key}`, _cloneDeep(payload.value));
      }
      else {
        _set(obj, `${payload.key}`, payload.value);
      }
    }
  });
  handleDirtyState(state, payload.userInitiated);
};


// eslint-disable-next-line
const update_collection_multiple_values = (state, payload) => {
  const { collection, keys, skipReactivity = false } = payload;
  collection.forEach((obj) => {
    const target = skipReactivity ? toRaw(obj) : obj;
    for (const [key, value] of Object.entries(keys)) {
      removeMessagesFromObj(target, key);
      _set(target, key, value);
    }
  });
  handleDirtyState(state, payload.userInitiated);
};

/**
 *
 * @param {*} state
 * @param {*} payload
 * {
 *   ids: {
*      id1: value1,
*      id2: value2,
 *   },
 *   pathToCollection: string
 * }
 */
const update_collection_unique_values = (state, payload) => {
  const { keys, path } = payload;
  const stateObject = _get(state, path);
  for (const item of stateObject) {
    const matchedItem = keys[item._id];
    if (!matchedItem) continue;
    for (const key of Object.keys(matchedItem)) {
      removeMessagesFromObj(item, key);
      _set(item, key, matchedItem[key]);
    }
  }
  handleDirtyState(state, payload.userInitiated);
};


const add_collection = (state, { path, collection, userInitiated }) => {
  if (!Array.isArray(_get(state, path))) _set(state, path, []); // Guard against pushing into non-array like properties when pushing into a collection.
  const stateObject = _get(state, path);
  stateObject.push(...collection);
  handleDirtyState(state, userInitiated);
};


const remove_collection = (state, payload) => {
  const stateObject = _get(state, payload.path);
  for (let index = 0; index < payload.collection.length; index += 1) {
    _remove(stateObject, payload.collection[index]);
  }
  handleDirtyState(state, payload.userInitiated);
};


// Helper function for remove_messages
const removeKey = (mdl, field, key) => {
  if (Object.prototype.hasOwnProperty.call(mdl, `${field}${key}`)) {
    _unset(mdl, `${field}${key}`);
  }
};


// Helper function for remove_messages
const removeFieldMessages = ({ model, propertyName, alsoKnownAs, fields, messageKeys }) => {
  if (!model) return model;
  const localFields = propertyName && [propertyName] ||
    fields ||
    Object.keys(model).filter(k => {
      return messageKeys.some(msgKey => {
        return k.endsWith(`_${msgKey}`);
      });
    });
  if (alsoKnownAs) localFields.push(alsoKnownAs);
  for (const f of localFields) {
    let field = f;
    if (!propertyName && !fields) [, field] = f.split('_');
    for (const mKey of messageKeys) removeKey(model, `_${field}`, `_${mKey}`);
  }
  return model;
};

/**
 * Removes messages in a similar fashion to coalescing (e.g. whether a scope of
 * object, field, or association as well as whether or not a specific field is passed).
 * @param {Object} state - Consists of Vuex state
 * @param {Object} payload - Information pertinent to removal of messages within state
 * @param {String} payload.type - Consists of type (e.g. errors/warnings, typically just errors).
 * @param {String} payload.scope - Scope of messages to be removed. Scope values are: object,
 *                                 field, or association.
 * @param {String} payload.fields - Optional parameter of specific field level messages to be
 *                                  removed from state object.
 * @param {String} payload.path - Path within state to remove messages from state.
 * @param {Boolean} payload.deep - Optional parameter to remove messages from nested objects.
 * @param {Object} payload.model - Optional parameter to remove messages from specific model.
 * @param {Object} payload.parentModel - Parent model to remove messages from, if checking for parent messages.
 * @param {String} payload.propertyName - Name of property to remove messages for, if checking for parent messages.
 */
const remove_messages = (state, payload) => {
  const { type, scope, fields = null, path, deep = false, model = null, parentModel = null, propertyName = '', alsoKnownAs = '' } = payload;
  const mKeys = type ? [type] : ['errors', 'warnings'];
  // Remove messages from parent model if checking for parent messages
  // and if we have the necessary information to do so
  if (parentModel && (propertyName || alsoKnownAs)) {
    removeFieldMessages({
      model: parentModel,
      messageKeys: mKeys,
      propertyName,
      alsoKnownAs,
    });
  }
  let stateObject = model || _get(state, path);
  if (!Array.isArray(stateObject)) stateObject = [stateObject];
  for (const obj of stateObject) {
    if (!isPresent(obj)) continue;
    if (scope === 'field' || scope === 'association') {
      // Removes messages for passed fields or all field messages if no fields are passed
      stateObject = removeFieldMessages({
        model: obj,
        messageKeys: mKeys,
        fields,
      });
    }
    if (scope === 'object' || scope === 'association') {
      // Remove '_errors' and/or '_warnings'
      for (const mKey of mKeys) removeKey(obj, '', `_${mKey}`);
    }
    // Recursively remove messages from nested objects
    if (deep) {
      for (const v of Object.values(obj)) {
        if (v && typeof v === 'object') {
          if (Array.isArray(v)) {
            for (const item of v) {
              if (!isPresent(item._classname)) continue;
              remove_messages(state, { ...payload, model: item, parentModel: null });
            }
          } else if (v._classname) remove_messages(state, { ...payload, model: v, parentModel: null });
        }
      }
    }
  }
};

/**
 * @param {String} className - string used to get params for matching className.
 * @return {String[]} Array set to also_managing params for auto-updates.
 */
const alsoManaging = (className) => {
  if (!ALSO_MANAGING[className]) return [];
  return ALSO_MANAGING[className]();
};


export {
  AUTOUPDATE_BACKREFS,
  alsoManaging,
  defaultKeys,
  updateValue,
  updateMultipleValues,
  updateCollection,
  updateCollectionMultipleValues,
  updateCollectionUniqueValues,
  addCollection,
  removeCollection,
  update_object,
  update_multiple_values,
  update_collection,
  update_collection_multiple_values,
  update_collection_unique_values,
  add_collection,
  remove_collection,
  insertKeys,
  insertKey,
  pruneDeletedObjects,
  generatePk,
  primaryKeys,
  remove_messages,
  removeMessages,
  sequencedKeys,
};
